import React, { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "src/components/ui/dialog"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Label } from "src/components/ui/label"
import { Switch } from "src/components/ui/switch"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "src/components/ui/select"
import { useToast } from "src/components/ui/toast"
import { useUpdateDocument } from "../hooks/useDocuments"
import { CATEGORY_LABELS } from "../constants"

// Edit an existing document's metadata (title, category, expiry, sharing).
// Mirrors UploadDocumentModal but operates on an existing record — no file
// picker. `document` is the document being edited (also drives `open`).
export function EditDocumentModal({
  document, open, onOpenChange, categories = Object.keys(CATEGORY_LABELS), onUpdated,
}) {
  const { toast } = useToast()
  const update = useUpdateDocument()

  const [title, setTitle] = useState("")
  const [category, setCategory] = useState("")
  const [expiresAt, setExpiresAt] = useState("")
  const [shareable, setShareable] = useState(false)
  const [saving, setSaving] = useState(false)

  // Hydrate the form whenever a new document is opened for editing.
  useEffect(() => {
    if (!document) return
    setTitle(document.title || "")
    setCategory(document.category || "")
    setExpiresAt(document.expires_at ? document.expires_at.slice(0, 10) : "")
    setShareable(!!document.is_shareable_with_customer)
  }, [document])

  async function handleSubmit() {
    if (!document) return
    setSaving(true)
    try {
      await update(document.id, {
        title: title || null,
        category: category || null,
        expires_at: expiresAt || null,
        is_shareable_with_customer: shareable,
      })
      toast({ title: "Document updated" })
      onOpenChange(false)
      onUpdated?.()
    } catch (err) {
      toast({ variant: "destructive", title: "Update failed", description: err.response?.data?.errors?.[0] })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit document</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-doc-title">Title</Label>
            <Input
              id="edit-doc-title" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder={document?.file_name || "Document title"}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger><SelectValue placeholder="Uncategorized" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-doc-expiry">Expires on</Label>
              <Input id="edit-doc-expiry" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-800">Shareable with customer</div>
              <div className="text-xs text-slate-400">Allow sending this document to leads / buyers</div>
            </div>
            <Switch checked={shareable} onCheckedChange={setShareable} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="gap-1.5">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
